import { Dispatch, FC, Fragment, SetStateAction, useMemo } from 'react';

import { CostCategoryTypeDto, SimulatePriceSimulationResponse } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
// eslint-disable-next-line dxp-rules/no-dialog-from-sparky
import { Box, Dialog, Divider, Grid, Heading, Text } from '@eneco/sparky';
import { Fields } from '@sitecore/types/PriceSimulation';

type PriceSimulationDetailsDialogProps = {
  fields: Fields;
  data: SimulatePriceSimulationResponse | undefined | null;
  showDetailsIndex: number;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const PriceSimulationDetailsDialog: FC<PriceSimulationDetailsDialogProps> = ({
  fields,
  data,
  showDetailsIndex,
  isOpen,
  setOpen,
}) => {
  const { currency } = useFormatter();

  const selectedProductCombinationOption = useMemo(
    () => data?.productCombinationOptions?.[showDetailsIndex],
    [data, showDetailsIndex],
  );

  const selectedProductKey = useMemo(() => {
    const electricityProduct = selectedProductCombinationOption?.electricityProduct;
    const gasProduct = selectedProductCombinationOption?.gasProduct;

    return [
      showDetailsIndex,
      electricityProduct?.productName,
      electricityProduct?.tarriffChartUrl,
      gasProduct?.productName,
      gasProduct?.tarriffChartUrl,
    ]
      .filter(Boolean)
      .join('-');
  }, [selectedProductCombinationOption, showDetailsIndex]);

  const tableRowsData = useMemo(() => {
    if (!selectedProductCombinationOption) return;

    const gas = selectedProductCombinationOption.gasProduct;
    const elec = selectedProductCombinationOption?.electricityProduct;

    // Prefer TotalWithPromo over Total when available
    const findCost = (product: typeof elec, type: CostCategoryTypeDto) => {
      if (type === 'Total') {
        const totalWithPromo = product?.costs.find(x => x.costCategoryType === 'TotalCostWithNonConditionalPromo');
        if (totalWithPromo) return totalWithPromo;
      }
      return product?.costs.find(x => x.costCategoryType === type);
    };

    const getCost = (type: CostCategoryTypeDto) => ({
      elec: findCost(elec, type),
      gas: findCost(gas, type),
    });

    const costRows = [
      {
        background: 'backgroundPrimary',
        format: 'bold',
        label: fields.summaryContractCard.energyCostsLabel?.value ?? '[not in sitecore]',
        type: 'EnergyCost',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.fixedFeeLabel?.value ?? '[not in sitecore]',
        type: 'FixedFee',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.injectionLabel?.value,
        type: 'Injection',
        showNegativeValue: true,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.consumptionLabel?.value ?? '[not in sitecore]',
        type: 'Usage',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'bold',
        label: fields.summaryContractCard?.nonConditionalPromoLabel?.value ?? 'non conditional promo',
        type: 'NonConditionalPromo',
        showNegativeValue: true,
      },
      {
        background: 'backgroundPrimary',
        format: 'bold',
        label: fields.summaryContractCard.netTariffRatesLabel?.value ?? '[not in sitecore]',
        type: 'ExtraCosts',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.offtakeLabel?.value ?? 'Afname [not in sitecore]',
        type: 'Offtake',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.prosumerLabel?.value ?? '[not in sitecore]',
        type: 'ProsumerCost',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.dataManagementLabel?.value ?? '[not in sitecore]',
        type: 'DataManagement',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'regular',
        label: fields.summaryContractCard.capacityRateLabel?.value ?? '[not in sitecore]',
        type: 'Captar',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'bold',
        label: fields.summaryContractCard.taxesAndLeviesLabel?.value ?? '[not in sitecore]',
        type: 'TaxationCost',
        showNegativeValue: false,
      },
      {
        background: 'backgroundPrimary',
        format: 'bold',
        label: fields.summaryContractCard.totalLabel?.value ?? '[not in sitecore]',
        type: 'Total',
        showNegativeValue: false,
      },
      {
        background: 'backgroundVarFour',
        format: 'regular',
        label: fields.summaryContractCard.conditionalPromoLabel?.value,
        type: 'ConditionalPromo',
        showNegativeValue: true,
      },
    ] as const;

    return costRows.map(row => ({
      background: row.background,
      format: row.format,
      label: row.label,
      showNegativeValue: row.showNegativeValue,
      type: row.type,
      ...getCost(row.type),
    }));
  }, [selectedProductCombinationOption, fields]);

  const hasElectricityTotal = useMemo(() => {
    const totalRow = tableRowsData?.find(row => row.type === 'Total');
    return (totalRow?.elec?.value ?? 0) !== 0;
  }, [tableRowsData]);

  const hasGasTotal = useMemo(() => {
    const totalRow = tableRowsData?.find(row => row.type === 'Total');
    return (totalRow?.gas?.value ?? 0) !== 0;
  }, [tableRowsData]);

  return (
    <Dialog
      key={selectedProductKey}
      width="wide"
      title={
        <Heading as="h3" size="S">
          {fields.summaryContractCard?.rateCardTitle?.value ?? 'Vaste energiecontracten [not in sitecore]'}
        </Heading>
      }
      description={
        <StackContent
          key={selectedProductKey}
          fields={fields}
          tableRowsData={tableRowsData}
          hasElectricityTotal={hasElectricityTotal}
          hasGasTotal={hasGasTotal}
          currencyEuro={currency.euro}
        />
      }
      isOpen={isOpen}
      setOpen={setOpen}>
      <Dialog.Close asChild></Dialog.Close>
    </Dialog>
  );
};

type TableRowData = {
  format: 'bold' | 'regular';
  label: string | undefined;
  background: 'backgroundPrimary' | 'backgroundVarFour'; // No background or green
  showNegativeValue: boolean;
  type: CostCategoryTypeDto;
  elec?: {
    costCategoryType?: CostCategoryTypeDto;
    value?: number;
  };
  gas?: {
    costCategoryType?: CostCategoryTypeDto;
    value?: number;
  };
};

const StackContent = ({
  fields,
  tableRowsData,
  hasElectricityTotal,
  hasGasTotal,
  currencyEuro,
}: {
  fields: Fields;
  tableRowsData: TableRowData[] | undefined;
  hasElectricityTotal: boolean;
  hasGasTotal: boolean;
  currencyEuro: (value: number) => string;
}) => (
  <>
    <Text>
      {fields.summaryContractCard?.rateCardDescription?.value ??
        'Bij een vast energiecontract staat de prijs van je energie voor 1 jaar vast. Dat beschermt je tegen prijsschommelingen. Maar wanneer de prijzen op de energiemarkt dalen, dalen je tarieven niet mee. Je hebt meer zekerheid en weet perfect waar je aan toe bent. [not in sitecore]'}
    </Text>
    <Heading as="h4" size="S">
      {fields.summaryContractCard?.rateCardSubTitle?.value ?? 'Details [not in sitecore]'}
    </Heading>
    <Grid
      gridTemplateColumns={
        hasElectricityTotal && hasGasTotal
          ? 'auto auto auto'
          : hasElectricityTotal || hasGasTotal
            ? 'auto auto'
            : 'auto'
      }>
      <Box backgroundColor="backgroundSecondary" padding="2" borderTopLeftRadius={'s'} borderBottomLeftRadius={'s'}>
        <Heading as="h5" size="3XS">
          {fields.summaryContractCard?.tableHeaderEnergyTypeLabel?.value ?? 'EnergieType [not in sitecore]'}
        </Heading>
      </Box>
      {hasElectricityTotal && (
        <Box
          backgroundColor="backgroundSecondary"
          {...(hasGasTotal ? { paddingY: '2' } : { padding: '2' })}
          borderTopRightRadius={!hasGasTotal ? 's' : undefined}
          borderBottomRightRadius={!hasGasTotal ? 's' : undefined}>
          <Text align="right">
            <Heading as="h5" size="3XS">
              {fields.summaryContractCard?.tableHeaderElectricityLabel?.value ?? 'Elektriciteit [not in sitecore]'}
            </Heading>
          </Text>
        </Box>
      )}
      {hasGasTotal && (
        <Box backgroundColor="backgroundSecondary" padding="2" borderTopRightRadius={'s'} borderBottomRightRadius={'s'}>
          <Text align="right">
            <Heading as="h5" size="3XS">
              {fields.summaryContractCard?.tableHeaderGasLabel?.value ?? 'Gas [not in sitecore]'}
            </Heading>
          </Text>
        </Box>
      )}
      {tableRowsData?.map((row, index) => {
        if (row.showNegativeValue && (row.elec?.value ?? 0) === 0 && (row.gas?.value ?? 0) === 0) {
          return null;
        }

        const showDivider = row.format === 'bold' && index !== 0 && !row.type.includes('Promo');
        return (
          <Fragment key={`${row.type}-${row.label}`}>
            <Box
              paddingLeft="2"
              paddingY={1}
              backgroundColor={row.background}
              borderBottomLeftRadius="s"
              borderTopLeftRadius="s">
              {showDivider && (
                <Box paddingBottom="2">
                  <Divider emphasis="low" />
                </Box>
              )}
              <Text weight={row.format} align={'left'}>
                {row.label}
              </Text>
            </Box>
            {hasElectricityTotal && (
              <Box
                paddingY={1}
                {...(!hasGasTotal && { paddingRight: '2' })}
                backgroundColor={row.background}
                borderBottomRightRadius={!hasGasTotal ? 's' : undefined}
                borderTopRightRadius={!hasGasTotal ? 's' : undefined}>
                {showDivider && (
                  <Box paddingBottom="2">
                    <Divider emphasis="low" />
                  </Box>
                )}
                <Text weight={row.format} align={'right'}>
                  {currencyEuro(row.showNegativeValue ? -(row.elec?.value ?? 0) : (row.elec?.value ?? 0))}
                </Text>
              </Box>
            )}
            {hasGasTotal && (
              <Box
                paddingRight="2"
                paddingY={1}
                backgroundColor={row.background}
                borderBottomRightRadius={'s'}
                borderTopRightRadius={'s'}>
                {showDivider && (
                  <Box paddingBottom="2">
                    <Divider emphasis="low" />
                  </Box>
                )}
                <Text weight={row.format} align={'right'}>
                  {currencyEuro(row.showNegativeValue ? -(row.gas?.value ?? 0) : (row.gas?.value ?? 0))}
                </Text>
              </Box>
            )}
          </Fragment>
        );
      })}
    </Grid>
  </>
);

export default PriceSimulationDetailsDialog;
